import { Injectable, TemplateRef, signal, Signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PageAction } from '../../components/app-shell/page-action.model';

export type PageHeaderBackButton = {
  /** Label for the button. Defaults to 'Back'. */
  label?: string;

  /** Deterministic routerLink target (recommended). */
  link?: any[] | string;

  /** Prefer Location.back() / history navigation instead of routerLink. */
  preferHistoryBack?: boolean;

  /**
   * Optional default route to use when preferHistoryBack is enabled but there is no
   * usable in-app history (e.g., after refresh / deep-link).
   */
  fallbackUrl?: string;

  /** Optional click handler (e.g., unsaved-changes confirm). If set, AppNav won't auto-navigate. */
  onClick?: () => void;

  /** Controls label rendering (icon-only on mobile vs always). */
  display?: 'auto' | 'icon' | 'icon+label';
};

export type PageHeaderTitleBadge = {
  /** Short label shown inline next to the page title (e.g. "Syncing (2)"). */
  label: string;

  /** Optional detailed text shown as a native tooltip on hover. */
  tooltip?: string;
};

@Injectable({
  providedIn: 'root'
})
export class PageHeaderService {
  private actionsTemplate = signal<TemplateRef<any> | null>(null);
  private pinnedActionsTemplate = signal<TemplateRef<any> | null>(null);
  private menuActionsTemplate = signal<TemplateRef<any> | null>(null);
  private searchTemplate = signal<TemplateRef<any> | null>(null);
  private backButtonSignal = signal<PageHeaderBackButton | null>(null);
  private pageTitleSignal = signal<string | null>(null);
  private titleBadgeSignal = signal<PageHeaderTitleBadge | null>(null);
  private pageActionsSignal = signal<PageAction[] | null>(null);
  private breadcrumbLabelSubject = new BehaviorSubject<string | null>(null);

  /**
   * Observable signal containing the current page actions template
   */
  readonly actions: Signal<TemplateRef<any> | null> = this.actionsTemplate.asReadonly();

  /**
   * Always-visible actions: shown as toolbar buttons on desktop and as
   * full-width buttons in a second row on mobile. Never hidden or collapsed.
   */
  readonly pinnedActions: Signal<TemplateRef<any> | null> = this.pinnedActionsTemplate.asReadonly();

  /**
   * Template rendered inside the ⋯ overflow dropdown in the toolbar.
   * Provide <li> items; the dropdown wrapper is rendered by the nav component.
   */
  readonly menuActions: Signal<TemplateRef<any> | null> = this.menuActionsTemplate.asReadonly();

  /**
   * Observable signal containing the current page search template
   */
  readonly search: Signal<TemplateRef<any> | null> = this.searchTemplate.asReadonly();

  /**
   * Optional back button configuration for the sub-header.
   * Pages can set this to show a back button; null means hidden.
   */
  readonly backButton: Signal<PageHeaderBackButton | null> = this.backButtonSignal.asReadonly();

  /**
   * Page title to display in the sub-header toolbar.
   */
  readonly pageTitle: Signal<string | null> = this.pageTitleSignal.asReadonly();

  /**
   * Optional small badge rendered inline next to the page title
   * (e.g. to indicate background tasks running for the current page).
   */
  readonly titleBadge: Signal<PageHeaderTitleBadge | null> = this.titleBadgeSignal.asReadonly();

  /**
   * Declarative page actions consumed by AppShellComponent (new API).
   * Pages should prefer this over the template-based `actions` / `menuActions`
   * inputs above. Null means: no declarative actions configured for this page.
   */
  readonly pageActions: Signal<PageAction[] | null> = this.pageActionsSignal.asReadonly();

  /**
   * Observable for breadcrumb label changes
   * @deprecated Use pageTitle signal instead
   */
  readonly breadcrumbLabel$ = this.breadcrumbLabelSubject.asObservable();

  /**
   * Set the actions template for the current page
   * @param template The template to render in the page toolbar (right zone)
   */
  setActions(template: TemplateRef<any>): void {
    this.actionsTemplate.set(template);
  }

  /**
   * Clear the current page actions
   */
  clearActions(): void {
    this.actionsTemplate.set(null);
  }

  /**
   * Set always-visible actions. On desktop they appear as toolbar buttons;
   * on mobile they render full-width in a second row below the toolbar.
   */
  setPinnedActions(template: TemplateRef<any>): void {
    this.pinnedActionsTemplate.set(template);
  }

  clearPinnedActions(): void {
    this.pinnedActionsTemplate.set(null);
  }

  /**
   * Set the overflow menu actions template. Rendered as <li> items inside
   * a ⋯ dropdown appended after the primary actions in the toolbar.
   */
  setMenuActions(template: TemplateRef<any>): void {
    this.menuActionsTemplate.set(template);
  }

  clearMenuActions(): void {
    this.menuActionsTemplate.set(null);
  }

  /**
   * Set the search template for the current page
   * @param template The template to render in the page toolbar (center zone)
   */
  setSearch(template: TemplateRef<any>): void {
    this.searchTemplate.set(template);
  }

  /**
   * Clear the current page search
   */
  clearSearch(): void {
    this.searchTemplate.set(null);
  }

  /**
   * Set the back button config (pass null to hide).
   */
  setBackButton(config: PageHeaderBackButton | null): void {
    this.backButtonSignal.set(config);
  }

  /**
   * Clear / hide the back button.
   */
  clearBackButton(): void {
    this.backButtonSignal.set(null);
  }

  /**
   * Set the page title to display in the sub-header toolbar
   * @param title The title to display
   */
  setPageTitle(title: string | null): void {
    this.pageTitleSignal.set(title);
  }

  /**
   * Clear the page title
   */
  clearPageTitle(): void {
    this.pageTitleSignal.set(null);
  }

  /**
   * Set (or clear, with null) the badge shown inline next to the page title.
   */
  setTitleBadge(badge: PageHeaderTitleBadge | null): void {
    this.titleBadgeSignal.set(badge);
  }

  /**
   * Clear the title badge.
   */
  clearTitleBadge(): void {
    this.titleBadgeSignal.set(null);
  }

  /**
   * Set declarative page actions (new AppShell API).
   * Pass null/empty array to clear.
   */
  setPageActions(actions: PageAction[] | null): void {
    this.pageActionsSignal.set(actions && actions.length ? actions : null);
  }

  /**
   * Clear declarative page actions.
   */
  clearPageActions(): void {
    this.pageActionsSignal.set(null);
  }

  /**
   * Clear all toolbar templates (search and actions)
   * Call this on route changes to avoid stale content
   */
  clearAll(): void {
    this.actionsTemplate.set(null);
    this.pinnedActionsTemplate.set(null);
    this.menuActionsTemplate.set(null);
    this.searchTemplate.set(null);
    this.backButtonSignal.set(null);
    this.pageTitleSignal.set(null);
    this.titleBadgeSignal.set(null);
    this.pageActionsSignal.set(null);
  }

  /**
   * Set dynamic breadcrumb label for the current page
   * @param label The label to display in breadcrumbs
   */
  setBreadcrumbLabel(label: string): void {
    this.breadcrumbLabelSubject.next(label);
  }

  /**
   * Clear the breadcrumb label
   */
  clearBreadcrumbLabel(): void {
    this.breadcrumbLabelSubject.next(null);
  }

  /**
   * Get the current breadcrumb label
   */
  getBreadcrumbLabel(): string | null {
    return this.breadcrumbLabelSubject.value;
  }
}
